import React, { useMemo, useRef, useEffect } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { Box, Typography, Pressable } from '@/shared/design-system/primitives';
import { toLogicDate } from '@/utils/date-utils';
import { useDraggableScroll } from '@/apps/master-plan/shared/hooks/useDraggableScroll';

interface HorizontalDateScrollerProps {
    selectedValue: string;
    originDate: string; // The date when the picker was first opened
    onSelect: (val: string) => void;
}

/**
 * HorizontalDateScroller: High-fidelity horizontal scroller for Dates.
 * V1.1: Added vertical jitter protection and origin date markers.
 */
export const HorizontalDateScroller: React.FC<HorizontalDateScrollerProps> = ({ selectedValue, originDate, onSelect }) => {
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const { scrollerRef: dragRef, scrollHandlers } = useDraggableScroll();

    const data = useMemo(() => {
        const list: Date[] = [];
        const base = new Date();
        // 180 days history, 180 days future
        const start = new Date(base);
        start.setDate(start.getDate() - 180);
        
        for (let i = 0; i < 361; i++) {
            const d = new Date(start);
            d.setDate(d.getDate() + i);
            list.push(d);
        }
        return list;
    }, []);

    const selectedIndex = useMemo(() => {
        const selStr = new Date(selectedValue).toDateString();
        const idx = data.findIndex(d => d.toDateString() === selStr);
        return idx !== -1 ? idx : 180;
    }, [data, selectedValue]);

    useEffect(() => {
        const timer = setTimeout(() => {
            virtuosoRef.current?.scrollToIndex({
                index: selectedIndex,
                align: 'center',
                behavior: 'auto'
            });
        }, 100);
        return () => clearTimeout(timer);
    }, [selectedIndex]);

    const renderDateItem = (index: number) => {
        const date = data[index];
        if (!date) return null;

        const dateStr = toLogicDate(date);
        const isSelected = date.toDateString() === new Date(selectedValue).toDateString(); 
        const isToday = date.toDateString() === new Date().toDateString();
        const isOrigin = dateStr === originDate;
        const isFirstOfMonth = date.getDate() === 1;
        
        // Priority: Selected (White) > Today (Green) > Origin (Orange) > Default (Grey)
        const highlightColor = isSelected ? '#fff' : (isToday ? '#2ecc71' : (isOrigin ? '#f29b11' : '#bbb'));
        const fontWeight = isToday || isSelected || isOrigin ? '900' : '800';

        return (
            <div 
                onClick={() => onSelect(dateStr)} 
                style={{ 
                    minWidth: '44px',
                    width: '44px', 
                    height: '44px', 
                    borderRadius: '10px',
                    border: isSelected ? '2px solid #f29b11' : isFirstOfMonth ? '1px solid #444' : '1px solid #222', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    backgroundColor: isFirstOfMonth && !isSelected && !isToday ? 'rgba(255,255,255,0.02)' : 'transparent', 
                    transition: 'all 0.2s ease', 
                    position: 'relative',
                    margin: '0 3px',
                    cursor: 'pointer',
                    flexShrink: 0,
                    boxShadow: isToday && !isSelected ? 'inset 0 0 10px rgba(46, 204, 113, 0.05)' : 'none'
                }}
            >
                {isFirstOfMonth && !isToday && (
                    <span style={{ position: 'absolute', top: '-12px', fontSize: '6px', fontWeight: '900', color: '#f29b11', letterSpacing: '0.5px' }}>{date.toLocaleDateString([], { month: 'short' }).toUpperCase()}</span>
                )}
                
                <span style={{ fontSize: '8px', fontWeight: '800', textTransform: 'uppercase', color: isToday ? '#2ecc71' : isFirstOfMonth ? '#f29b11' : '#666', pointerEvents: 'none' }}>{date.toLocaleDateString([], { weekday: 'short' })}</span>
                <span style={{ fontSize: '17px', pointerEvents: 'none', fontWeight, color: highlightColor, fontFamily: 'JetBrains Mono, monospace', fontVariantNumeric: 'tabular-nums' }}>{date.getDate()}</span>
            </div>
        );
    };

    return (
        <Box style={{ height: 80, width: '100%', position: 'relative', marginTop: 10, overflow: 'hidden' }}>
            <div 
                {...scrollHandlers}
                style={{ 
                    height: '80px', 
                    width: '100%', 
                    overflow: 'hidden', 
                    display: 'flex', 
                    alignItems: 'center' 
                }}
            >
                <Virtuoso
                    ref={virtuosoRef}
                    initialTopMostItemIndex={selectedIndex}
                    scrollerRef={(ref) => {
                        // @ts-ignore
                        dragRef.current = ref;
                    }}
                    horizontalDirection
                    data={data}
                    totalCount={data.length}
                    itemContent={(index) => renderDateItem(index)}
                    style={{ height: '100%', width: '100%', overflowY: 'hidden' }}
                    className="no-scrollbar"
                    overscan={30}
                />
            </div>
            <style>{`
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>
        </Box>
    );
};
